import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/apiClient';

const ANALYTICS_DASHBOARD_CACHE_TTL_MS = 3 * 60 * 1000;
const ANALYTICS_ALERTS_CACHE_TTL_MS = 3 * 60 * 1000;
const ANALYTICS_DASHBOARD_CACHE = new Map();
const ANALYTICS_DASHBOARD_INFLIGHT = new Map();
const ANALYTICS_ALERTS_CACHE = new Map();
const ANALYTICS_ALERTS_INFLIGHT = new Map();
const isCypressTestRun = typeof window !== 'undefined' && Boolean(window.Cypress);

/**
 * Build canonical dashboard KPI fields from the analytics/kpis payload.
 *
 * @param {object} [kpis] - Raw KPI object from `/analytics/kpis/`
 * @returns {object} Canonical KPI map for card rendering (single source)
 */
export const buildCanonicalKpisFromPayload = (kpis = {}) => {
    const revenueRecognized = kpis.revenue_recognized ?? kpis.total_revenue ?? null;

    return {
        total_revenue: revenueRecognized,
        revenue_recognized: revenueRecognized,
        revenue_invoiced: kpis.revenue_invoiced ?? null,
        total_costs: kpis.total_costs ?? null,
        net_profit: kpis.net_profit ?? null,
        accounts_receivable: kpis.accounts_receivable ?? null,
        profit_margin: kpis.profit_margin ?? kpis.gross_margin ?? null,
        billable_utilization: kpis.billable_utilization ?? null,
        utilization_rate: kpis.billable_utilization ?? null,
        realization_rate: kpis.realization ?? null,
        backlog_amount: kpis.backlog_amount ?? null,
        dso_days: kpis.dso_days ?? null,
        wip_amount: kpis.wip_amount ?? null,
        active_projects: kpis.active_projects ?? null,
        total_projects: kpis.total_projects ?? null,
        completion_rate: kpis.completion_rate ?? null,
        data_accuracy: kpis.data_accuracy ?? null,
        forecast_accuracy: kpis.forecast_accuracy ?? null,
        reports_generated: kpis.reports_generated ?? null,
        active_insights: kpis.active_insights ?? null,
    };
};

/** Clears module-level caches (used by tests to prevent cross-example leakage). */
export const resetAnalyticsCaches = () => {
    ANALYTICS_DASHBOARD_CACHE.clear();
    ANALYTICS_DASHBOARD_INFLIGHT.clear();
    ANALYTICS_ALERTS_CACHE.clear();
    ANALYTICS_ALERTS_INFLIGHT.clear();
};

const toDateValue = (value, fallback = null) => {
    if (value == null) {
        return fallback;
    }
    if (typeof value?.toISOString === 'function') {
        return value.toISOString().split('T')[0];
    }
    return typeof value === 'string' ? value : fallback;
};

const normalizeFilterValue = (value) => {
    if (value == null || value === '' || value === 'all') {
        return null;
    }
    return `${value}`;
};

const buildDashboardRequestKey = ({
    userId,
    start_date,
    end_date,
    role,
    status,
    project_id,
    team_id,
    client_id
}) => JSON.stringify({
    userId,
    start_date: start_date || null,
    end_date: end_date || null,
    role: role || 'admin',
    status: normalizeFilterValue(status),
    project_id: normalizeFilterValue(project_id),
    team_id: normalizeFilterValue(team_id),
    client_id: normalizeFilterValue(client_id),
});

const buildAlertsRequestKey = ({ userId, start_date, end_date }) => JSON.stringify({
    userId,
    start_date: start_date || null,
    end_date: end_date || null
});

/** YYYY-MM-DD in the browser's local calendar (matches dashboard timeline helpers that avoid UTC drift). */
const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Convert time_range filter to start_date / end_date strings aligned with Unified PSA rolling windows.
 *
 * @param {string} timeRange — e.g. 30d, 90d, 180d, 365d, ytd
 * @returns {{ start_date: string, end_date: string }}
 */
export const convertTimeRangeToDates = (timeRange) => {
    const endDate = new Date();
    const startDate = new Date();

    switch (timeRange) {
        case '7d':
            startDate.setDate(endDate.getDate() - 7);
            break;
        case '30d':
            startDate.setDate(endDate.getDate() - 30);
            break;
        case '90d':
            startDate.setDate(endDate.getDate() - 90);
            break;
        case '180d':
            startDate.setDate(endDate.getDate() - 180);
            break;
        case '365d':
            startDate.setDate(endDate.getDate() - 365);
            break;
        case '1y':
            startDate.setFullYear(endDate.getFullYear() - 1);
            break;
        case 'ytd':
            startDate.setMonth(0, 1);
            break;
        default: {
            const match = typeof timeRange === 'string' ? /^(\d+)d$/.exec(timeRange.trim()) : null;
            if (match) {
                const days = Number.parseInt(match[1], 10);
                if (Number.isFinite(days) && days > 0) {
                    startDate.setDate(endDate.getDate() - days);
                    break;
                }
            }
            startDate.setDate(endDate.getDate() - 30);
        }
    }

    return {
        start_date: formatLocalDate(startDate),
        end_date: formatLocalDate(endDate)
    };
};

const isRequestCanceled = (error) => error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';

/**
 * Custom hook for managing analytics data and operations
 * Follows the established pattern of separating data logic from UI
 * Integrates with the Analytics Engine API endpoints
 */
export const useAnalytics = (initialFilters = {}) => {
    const { user, token } = useAuth();
    const userRole = user?.role;
    const includeTrends = initialFilters.includeTrends !== false;
    const latestRequestRef = useRef(0);
    const activeRequestRef = useRef({
        key: null,
        controller: null,
        seq: 0
    });
    const userId = user?.id || user?.username || 'anonymous';
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [dashboardData, setDashboardData] = useState(null);
    const [predictiveData, setPredictiveData] = useState(null);
    const [customReports, setCustomReports] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [filters, setFilters] = useState({
        start_date: null,
        end_date: null,
        role: user?.role || 'admin',
        time_range: initialFilters.time_range || '30d',
        forecast_period: initialFilters.forecast_period || 90,
        ...initialFilters
    });

    const mapProjectionKpis = useCallback((projectionKpis = {}) => {
        return {
            total_revenue: projectionKpis.revenue_recognized,
            revenue_invoiced: projectionKpis.revenue_invoiced,
            revenue_recognized: projectionKpis.revenue_recognized,
            total_costs: projectionKpis.total_costs,
            net_profit: projectionKpis.net_profit,
            accounts_receivable: projectionKpis.accounts_receivable,
            profit_margin: projectionKpis.profit_margin,
            utilization_rate: projectionKpis.billable_utilization,
            realization_rate: projectionKpis.realization,
            backlog_amount: projectionKpis.backlog_amount,
            dso_days: projectionKpis.dso_days,
            wip_amount: projectionKpis.wip_amount,
            active_projects: projectionKpis.active_projects,
            total_projects: projectionKpis.total_projects,
            completion_rate: projectionKpis.completion_rate,
            data_accuracy: projectionKpis.data_accuracy,
            forecast_accuracy: projectionKpis.forecast_accuracy,
            reports_generated: projectionKpis.reports_generated,
            active_insights: projectionKpis.active_insights,
        };
    }, []);

    /**
     * Fetch dashboard KPIs and trends from PSA-standard endpoints
     */
    const fetchDashboardData = useCallback(async (customFilters = null) => {
        if (!token) {
            setError('Authentication required');
            return;
        }

        try {
            // Use provided filters or current filters state
            const currentFilters = customFilters || filters;

            // Convert time_range to actual dates if start_date/end_date not provided
            let start_date, end_date;
            if (currentFilters.start_date && currentFilters.end_date) {
                start_date = toDateValue(currentFilters.start_date);
                end_date = toDateValue(currentFilters.end_date);
            } else {
                const dates = convertTimeRangeToDates(currentFilters.time_range || '30d');
                start_date = dates.start_date;
                end_date = dates.end_date;
            }

            const requestRole = normalizeFilterValue(currentFilters.role || userRole) || 'admin';
            const requestStatus = normalizeFilterValue(currentFilters.status);
            const requestProjectId = normalizeFilterValue(currentFilters.project_id);
            const requestTeamId = normalizeFilterValue(currentFilters.team_id);
            const requestClientId = normalizeFilterValue(currentFilters.client_id);
            const requestKey = buildDashboardRequestKey({
                userId,
                start_date,
                end_date,
                role: requestRole,
                status: requestStatus,
                project_id: requestProjectId,
                team_id: requestTeamId,
                client_id: requestClientId
            });

            const cachedEntry = !isCypressTestRun ? ANALYTICS_DASHBOARD_CACHE.get(requestKey) : null;
            if (cachedEntry && (Date.now() - cachedEntry.timestamp) < ANALYTICS_DASHBOARD_CACHE_TTL_MS) {
                setDashboardData(cachedEntry.dashboardData);
                setError(null);
                setLoading(false);
                return cachedEntry.dashboardData;
            }

            const inFlightRequest = isCypressTestRun ? null : ANALYTICS_DASHBOARD_INFLIGHT.get(requestKey);
            if (inFlightRequest) {
                return inFlightRequest;
            }

            if (
                activeRequestRef.current.controller &&
                activeRequestRef.current.key !== requestKey
            ) {
                activeRequestRef.current.controller.abort();
            }

            const requestSeq = ++latestRequestRef.current;
            const controller = new AbortController();
            const isLatestRequest = () => requestSeq === latestRequestRef.current;
            const requestConfig = { signal: controller.signal };
            activeRequestRef.current = {
                key: requestKey,
                controller,
                seq: requestSeq
            };

            const doFetch = async () => {
                try {
                    setLoading(true);
                    setError(null);

                    const kpiParams = {
                        start_date,
                        end_date,
                        role: requestRole,
                        ...(requestStatus !== null ? { status: requestStatus } : {}),
                        ...(requestProjectId !== null ? { project_id: requestProjectId } : {}),
                        ...(requestTeamId !== null ? { team_id: requestTeamId } : {}),
                        ...(requestClientId !== null ? { client_id: requestClientId } : {}),
                    };
                    const trendParams = {
                        start_date,
                        end_date,
                        metrics: 'revenue,utilization,margin',
                        interval: 'month'
                    };
                    // KPIs + trends in parallel only when trend data is needed.
                    const trendsRequest = includeTrends
                        ? apiClient.get('/analytics/trends/', { params: trendParams, ...requestConfig })
                        : Promise.resolve({ data: {} });
                    const [kpiRes, trendsRes] = await Promise.all([
                        apiClient.get('/analytics/kpis/', { params: kpiParams, ...requestConfig }),
                        trendsRequest
                    ]);

                    if (!isLatestRequest()) {
                        return;
                    }

                    const kpiData = (kpiRes && kpiRes.data) ? kpiRes.data : {};
                    const projectionKpis = kpiData.projection?.kpis || {};
                    const mappedProjectionKpis = mapProjectionKpis(projectionKpis);
                    const kpis = kpiData.kpis || {};
                    const hasProjectionKpis = projectionKpis && Object.keys(projectionKpis).length > 0;
                    const quality = kpiData.quality || null;
                    const rawQualityWarnings = quality?.warnings || [];
                    const rawRequiredData = quality?.required_data || [];
                    const uniqueStringsPreserveOrder = (values) => {
                        const seen = new Set();
                        const out = [];
                        (Array.isArray(values) ? values : []).forEach((item) => {
                            if (typeof item !== 'string') {
                                return;
                            }
                            const trimmed = item.trim();
                            if (!trimmed || seen.has(trimmed)) {
                                return;
                            }
                            seen.add(trimmed);
                            out.push(trimmed);
                        });
                        return out;
                    };
                    const qualityWarnings = uniqueStringsPreserveOrder(rawQualityWarnings);
                    const requiredData = uniqueStringsPreserveOrder(rawRequiredData);
                    const requestFilters = {
                        start_date,
                        end_date,
                        role: requestRole,
                        ...(requestStatus !== null ? { status: requestStatus } : {}),
                        ...(requestTeamId !== null ? { team_id: requestTeamId } : {}),
                        ...(requestClientId !== null ? { client_id: requestClientId } : {}),
                    };

                    const trendsData = includeTrends ? (trendsRes && trendsRes.data ? trendsRes.data : {}) : {};
                    const series = trendsData.series || [];
                    const dataQuality = trendsData.data_quality || null;
                    const trendFilters = {
                        start_date,
                        end_date,
                        metrics: 'revenue,utilization,margin',
                        interval: 'month'
                    };
                    const monthlyRevenue = {};
                    const monthlyUtilization = {};
                    const monthlyMargin = {};
                    series.forEach(point => {
                        if (typeof point.revenue === 'number') {
                            monthlyRevenue[point.period] = point.revenue;
                        }
                        if (typeof point.utilization === 'number') {
                            monthlyUtilization[point.period] = point.utilization;
                        }
                        if (typeof point.margin === 'number') {
                            monthlyMargin[point.period] = point.margin;
                        }
                    });

                    const canonicalKpis = buildCanonicalKpisFromPayload(kpis);
                    const dashboard = {
                        kpis: canonicalKpis,
                        projectionKpis: hasProjectionKpis ? mappedProjectionKpis : null,
                        kpiMetadata: kpiData.kpi_metadata || kpiData.projection?.metadata || {},
                        trends: includeTrends ? {
                            revenue_trend: {
                                monthly_data: monthlyRevenue,
                                validation: dataQuality ? {
                                    isValid: dataQuality.valid,
                                    warnings: dataQuality.issues || [],
                                    completeness: dataQuality.completeness,
                                    filledGaps: 0,  // Frontend can fill gaps if needed
                                    endpoint: '/analytics/trends/',
                                    source_tag: 'analytics/trends',
                                    filters: trendFilters
                                } : null
                            },
                            utilization_trend: {
                                monthly_data: monthlyUtilization,
                                validation: dataQuality ? {
                                    ...(dataQuality || {}),
                                    endpoint: '/analytics/trends/',
                                    source_tag: 'analytics/trends',
                                    filters: trendFilters
                                } : null
                            },
                            margin_trend: {
                                monthly_data: monthlyMargin,
                                validation: dataQuality ? {
                                    ...(dataQuality || {}),
                                    endpoint: '/analytics/trends/',
                                    source_tag: 'analytics/trends',
                                    filters: trendFilters
                                } : null
                            }
                        } : null,
                        dateRange: { start_date, end_date }, // Store date range for change detection
                        quality: {
                            ...(quality || {}),
                            endpoint: '/analytics/kpis/',
                            source_tag: 'analytics/kpis',
                            filters: requestFilters,
                            warnings: qualityWarnings,
                            required_data: requiredData,
                        },
                        metadata: kpiData.metadata || null
                    };
                    setDashboardData(dashboard);
                    ANALYTICS_DASHBOARD_CACHE.set(requestKey, {
                        timestamp: Date.now(),
                        dashboardData: dashboard
                    });
                } catch (err) {
                    if (isLatestRequest() && !isRequestCanceled(err)) {
                        console.error('Error fetching dashboard data:', err);
                        setError(err.response?.data?.detail || 'Failed to load dashboard data');
                    }
                } finally {
                    if (isLatestRequest()) {
                        setLoading(false);
                    }
                    if (activeRequestRef.current.key === requestKey && activeRequestRef.current.seq === requestSeq) {
                        activeRequestRef.current = {
                            key: null,
                            controller: null,
                            seq: 0
                        };
                    }
                }
            };

            const inFlightRequestPromise = doFetch();
            ANALYTICS_DASHBOARD_INFLIGHT.set(requestKey, inFlightRequestPromise);
            return inFlightRequestPromise.finally(() => {
                ANALYTICS_DASHBOARD_INFLIGHT.delete(requestKey);
            });
        } catch (err) {
            if (!isRequestCanceled(err)) {
                console.error('Error fetching dashboard data:', err);
                setError(err.response?.data?.detail || 'Failed to load dashboard data');
            }
        }
    }, [mapProjectionKpis, token, userRole, userId, filters, includeTrends]);

    /**
     * Fetch predictive analytics using historical data
     * Integrates with analytics-engine predictive analytics endpoint
     */
    const fetchPredictiveAnalytics = useCallback(async (dataToUse = null) => {
        if (!token) {
            return;
        }

        const data = dataToUse || dashboardData;
        if (!data) {
            return;
        }

        try {
            // Create proper historical data from dashboard data with actual trend values
            const historicalData = [];

            if (data?.trends?.revenue_trend?.monthly_data) {
                // Get all periods from revenue trend
                const periods = Object.keys(data.trends.revenue_trend.monthly_data).sort();

                periods.forEach(period => {
                    const hasRevenue = Object.prototype.hasOwnProperty.call(data.trends.revenue_trend.monthly_data, period);
                    const revenue = hasRevenue ? data.trends.revenue_trend.monthly_data[period] : null;
                    // Use actual utilization trend data for each period, not a single value
                    const hasUtilization = data.trends.utilization_trend && Object.prototype.hasOwnProperty.call(data.trends.utilization_trend.monthly_data || {}, period);
                    const utilization = hasUtilization ? data.trends.utilization_trend.monthly_data[period] : null;

                    historicalData.push({
                        period: period,
                        revenue: revenue,
                        utilization: utilization,
                        project_count: typeof data.kpis?.active_projects === 'number' ? data.kpis.active_projects : null
                    });
                });
            }

            if (historicalData.length === 0) {
                // No sufficient data to compute predictive analytics
                setPredictiveData(null);
                return;
            }

            const response = (await apiClient.post('/analytics/forecasts/', {
                historical_data: historicalData,
                forecast_period: Math.ceil((filters.forecast_period || 90) / 30) // Convert days to months
            })) || {};
            const responseData = response?.data || {};

            // Add confidence and methodology from response
            setPredictiveData({
                ...responseData,
                method: responseData.method || 'moving_average'
            });
        } catch (err) {
            console.error('Error fetching predictive analytics:', err);
            setPredictiveData(null);
        }
    }, [dashboardData, token, filters.forecast_period]);

    /**
     * Generate custom reports
     * Integrates with analytics-engine custom reports endpoint
     */
    const generateCustomReport = useCallback(async (reportType) => {
        if (!token) {
            setError('Authentication required');
            return;
        }

        try {
            setLoading(true);

            let start_date, end_date;
            if (filters.start_date && filters.end_date) {
                start_date = filters.start_date?.toISOString?.()?.split('T')[0] || filters.start_date;
                end_date = filters.end_date?.toISOString?.()?.split('T')[0] || filters.end_date;
            } else {
                const dates = convertTimeRangeToDates(filters.time_range || '30d');
                start_date = dates.start_date;
                end_date = dates.end_date;
            }

            const response = await apiClient.post('/analytics/custom-reports/', {
                report_config: {
                    type: reportType,
                    filters: {
                        start_date: start_date,
                        end_date: end_date
                    },
                    metrics: reportType === 'financial_performance'
                        ? ['revenue', 'profit', 'margin']
                        : ['utilization', 'billable_hours']
                }
            });

            const reportPayload = response?.data?.report || {};
            const newReport = {
                ...reportPayload,
                id: Date.now(),
                generated_at: reportPayload.generated_at || new Date().toISOString(),
                type: reportType
            };

            setCustomReports(prev => [...prev, newReport]);
            return newReport;
        } catch (err) {
            console.error('Error generating custom report:', err);
            setError(err.response?.data?.detail || 'Failed to generate custom report');
            return null;
        } finally {
            setLoading(false);
        }
    }, [filters, token]);

    /**
     * Fetch business intelligence alerts
     * Integrates with PSA-standard alerts endpoint
     *
     * @param {object|null} customFilters Optional merged filters (same shape as `filters`) so alerts can run in parallel with KPI fetches after `updateFilters` without waiting on React state commit.
     */
    const fetchAlerts = useCallback(async (customFilters = null) => {
        if (!token) {
            return;
        }

        try {
            const effectiveFilters = customFilters || filters;
            let start_date, end_date;
            if (effectiveFilters.start_date && effectiveFilters.end_date) {
                start_date = toDateValue(effectiveFilters.start_date);
                end_date = toDateValue(effectiveFilters.end_date);
            } else {
                const dates = convertTimeRangeToDates(effectiveFilters.time_range || '30d');
                start_date = dates.start_date;
                end_date = dates.end_date;
            }
            const requestKey = buildAlertsRequestKey({
                userId,
                start_date,
                end_date
            });
            const cachedAlerts = ANALYTICS_ALERTS_CACHE.get(requestKey);
            if (cachedAlerts && (Date.now() - cachedAlerts.timestamp) < ANALYTICS_ALERTS_CACHE_TTL_MS) {
                setAlerts(cachedAlerts.alerts);
                return cachedAlerts.alerts;
            }

            const inFlightRequest = ANALYTICS_ALERTS_INFLIGHT.get(requestKey);
            if (inFlightRequest) {
                return inFlightRequest;
            }

            const doFetch = async () => {
                const response = await apiClient.get('/analytics/alerts/', { params: { start_date, end_date } });
                const alertsList = response.data?.alerts || [];
                setAlerts(alertsList);
                ANALYTICS_ALERTS_CACHE.set(requestKey, {
                    timestamp: Date.now(),
                    alerts: alertsList
                });
                return alertsList;
            };
            const inFlightRequestPromise = doFetch();
            ANALYTICS_ALERTS_INFLIGHT.set(requestKey, inFlightRequestPromise);
            try {
                return await inFlightRequestPromise;
            } finally {
                if (ANALYTICS_ALERTS_INFLIGHT.get(requestKey) === inFlightRequestPromise) {
                    ANALYTICS_ALERTS_INFLIGHT.delete(requestKey);
                }
            }
        } catch (err) {
            console.error('Error fetching alerts:', err);
            setAlerts([]);
            setError(err.response?.data?.detail || 'Failed to load BI alerts');
        }
    }, [filters, token, userId]);

    /**
     * Update filters and trigger data refresh
     */
    const updateFilters = useCallback((newFilters) => {
        setFilters(prev => {
            const updated = { ...prev, ...newFilters };
            if (token) {
                fetchDashboardData(updated);
                fetchAlerts(updated);
            }
            return updated;
        });
    }, [token, fetchDashboardData, fetchAlerts]);

    /**
     * Refresh all analytics data
     */
    const refreshAnalytics = useCallback(async () => {
        await Promise.all([
            fetchDashboardData(filters),
            fetchAlerts(filters),
        ]);
    }, [fetchDashboardData, fetchAlerts, filters]);

    /**
     * Fetch detailed data for a specific period (for drilldown)
     */
    const fetchPeriodDetails = useCallback(async (period) => {
        if (!token || !period) {
            return null;
        }

        try {
            const response = await apiClient.get('/analytics/period-details/', {
                params: { period }
            });
            return response?.data ?? null;
        } catch (err) {
            console.error('Error fetching period details:', err);
            return null;
        }
    }, [token]);

    // Auto-fetch predictive analytics when dashboard data date range changes
    const dashboardStartDate = dashboardData?.dateRange?.start_date;
    const dashboardEndDate = dashboardData?.dateRange?.end_date;
    useEffect(() => {
        if (dashboardData && dashboardStartDate && dashboardEndDate && !loading && token) {
            const timeoutId = setTimeout(() => {
                fetchPredictiveAnalytics(dashboardData);
            }, 500);

            return () => clearTimeout(timeoutId);
        }
    }, [dashboardData, dashboardStartDate, dashboardEndDate, loading, token, fetchPredictiveAnalytics]);

    /**
     * Single bootstrap fetch when auth becomes ready.
     * Timeline and filter updates go through updateFilters(), which already calls fetchDashboardData.
     * (Replacing the previous mount + debounced pair avoided duplicate /analytics/kpis + /analytics/trends calls.)
     */
    const analyticsBootstrapRef = useRef(false);
    useEffect(() => {
        if (!token || !user) {
            analyticsBootstrapRef.current = false;
            return;
        }
        if (analyticsBootstrapRef.current) {
            return;
        }
        analyticsBootstrapRef.current = true;
        void Promise.all([
            fetchDashboardData(),
            fetchAlerts(),
        ]).catch((err) => {
            if (!isRequestCanceled(err)) {
                console.error('Analytics bootstrap failed:', err);
            }
        });
    }, [token, user, fetchDashboardData, fetchAlerts]);

    // Utility functions for formatting
    const formatCurrency = useCallback((value) => {
        if (value === null || value === undefined || !Number.isFinite(value)) return 'N/A';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    }, []);

    const formatPercentage = useCallback((value) => {
        if (value === null || value === undefined || !Number.isFinite(value)) return 'N/A';
        return `${value.toFixed(1)}%`;
    }, []);

    return {
        // Data
        dashboardData,
        predictiveData,
        customReports,
        alerts,
        filters,
        user,

        // State
        loading,
        error,

        // Operations
        fetchDashboardData,
        fetchPredictiveAnalytics,
        generateCustomReport,
        fetchAlerts,
        updateFilters,
        refreshAnalytics,
        fetchPeriodDetails,

        // Utilities
        formatCurrency,
        formatPercentage
    };
}; 